'use client';
import { isMobile } from '@/utils/devices';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { addMonths } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCookie, useEvent, useMedia } from 'react-use';
import { DialogCloseButton } from './DialogCloseButton';
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
}

const oneMonthFromNow = addMonths(new Date(), 1);

export const InstallPwaDialog = () => {
  const { t } = useTranslation();
  const windowApi = typeof window !== 'undefined' ? window : undefined;
  const isWindowControlsOverlay = useMedia(
    '(display-mode: window-controls-overlay)',
    false
  );
  const isStandalone = useMedia('(display-mode: standalone)', false);
  const isInsideInstalledPWA = isWindowControlsOverlay || isStandalone;

  const [isAlreadyInstalled, setIsAlreadyInstalled] = useState(false);
  const [isBrowserAllowPwa, setIsBrowserAllowPwa] = useState(false);
  const [installPromptEvent, setInstallPromptEvent] =
    useState<BeforeInstallPromptEvent | null>();

  const [isPwaDialogSeenCookie, updateIsPwaDialogSeenCookie] =
    useCookie('isPwaDialogSeen');
  const isPwaDialogSeen = isPwaDialogSeenCookie === 'true';

  useEvent(
    'appinstalled',
    (event: Event) => {
      event.preventDefault();
      setIsAlreadyInstalled(true);
    },
    windowApi
  );

  useEvent(
    'beforeinstallprompt',
    (event: Event) => {
      event.preventDefault();
      setIsBrowserAllowPwa(true);
      setInstallPromptEvent(event as BeforeInstallPromptEvent);
    },
    windowApi
  );

  const handlePwaDialogSeen = () => {
    updateIsPwaDialogSeenCookie('true', {
      expires: oneMonthFromNow,
      sameSite: 'Lax',
    });
  };

  const handleInstallClick = async () => {
    if (!installPromptEvent) return;
    await installPromptEvent.prompt();
    handlePwaDialogSeen();
  };

  const isMobileDevice = isMobile();

  const isOpen =
    isMobileDevice &&
    !isPwaDialogSeen &&
    !isAlreadyInstalled &&
    !isInsideInstalledPWA &&
    isBrowserAllowPwa;

  return (
    <Dialog open={isOpen} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography variant={'h5'} noWrap color="text.primary">
          {t('add_to_home_screen')}
        </Typography>
        <DialogCloseButton onClick={handlePwaDialogSeen} />
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          paddingBlock: 2,
        }}>
        <Typography variant={'body1'}>
          {t(
            'add_a_shortcut_for_treyd_to_your_home_screen_to_access_it_faster_on_the_go'
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button size="large" variant="contained" onClick={handleInstallClick}>
          {t('install')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
